import { IconButton, MenuItem, Select, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Panel from '../reusable/Panel';
import AddIcon from '../assets/eoa-configuration-add.svg';
import { decimalNumberError, letterGradeError, wholeNumberError } from './fieldValidation';
import {
  GRADING_SYSTEM_OPTIONS,
  GradingForm,
  GradingSystem,
  addGradeMap,
  removeGradeMap,
  setFailingLetter,
  setFailingNumber,
  updateGradeMap,
} from './gradingForm';

interface GradingFieldsProps {
  value: GradingForm;
  onChange: (value: GradingForm) => void;
}

const placeholder = 'Your text here';

const GradingFields = ({ value, onChange }: GradingFieldsProps) => {
  const failingNumberHelperText = wholeNumberError(value.failingNumber);
  const failingLetterHelperText = letterGradeError(value.failingLetter);

  return (
    <Panel className="eoa-configuration-card">
      <h3 className="eoa-configuration-card-title">Grading</h3>
      <div className="eoa-configuration-fields">
        <div className="eoa-configuration-field eoa-configuration-field--medium">
          <span className="eoa-configuration-field-label">What is your default number of credits per course?</span>
          <TextField
            placeholder={'Number'}
            value={value.defaultCredits}
            onChange={(event) => onChange({ ...value, defaultCredits: event.target.value })}
          />
        </div>

        <div className="eoa-configuration-field">
          <span className="eoa-configuration-field-label">What is your grading system?</span>
          <Select
            className="eoa-configuration-alias-period-select"
            variant="standard"
            displayEmpty
            value={value.gradingSystem}
            onChange={(event) => onChange({ ...value, gradingSystem: event.target.value as GradingSystem })}
            renderValue={(selected) =>
              GRADING_SYSTEM_OPTIONS.find((option) => option.value === selected)?.label ?? 'Value'
            }
          >
            {GRADING_SYSTEM_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </div>

        <div className="eoa-configuration-field">
          <span className="eoa-configuration-field-label">
            What is your numeric and / or letter grade threshold for failure?
          </span>
          <div className="eoa-configuration-inline-row">
            <TextField
              className="eoa-configuration-narrow-input"
              placeholder="Number grade"
              inputMode="numeric"
              value={value.failingNumber}
              error={Boolean(failingNumberHelperText)}
              helperText={failingNumberHelperText}
              onChange={(event) => onChange(setFailingNumber(value, event.target.value))}
            />
            <span className="eoa-configuration-maps-to">or</span>
            <TextField
              className="eoa-configuration-narrow-input"
              placeholder="Letter grade"
              value={value.failingLetter}
              error={Boolean(failingLetterHelperText)}
              helperText={failingLetterHelperText}
              onChange={(event) => onChange(setFailingLetter(value, event.target.value))}
            />
          </div>
        </div>

        <div className="eoa-configuration-field eoa-configuration-field--medium">
          <span className="eoa-configuration-field-label">How do your credit hours map to Carnegie Units?</span>
          <TextField
            placeholder={placeholder}
            value={value.carnegieUnits}
            onChange={(event) => onChange({ ...value, carnegieUnits: event.target.value })}
          />
        </div>

        <div className="eoa-configuration-field">
          <span className="eoa-configuration-field-label">How do you map your letter grades to number grades?</span>
          <div className="eoa-configuration-removable-list">
            {value.gradeMaps.map((row) => {
              const letterHelperText = letterGradeError(row.letter);
              const numberHelperText = decimalNumberError(row.number);

              return (
                <div key={row.id} className="eoa-configuration-inline-row">
                  <TextField
                    className="eoa-configuration-narrow-input"
                    placeholder="Letter grade"
                    value={row.letter}
                    error={Boolean(letterHelperText)}
                    helperText={letterHelperText}
                    onChange={(event) => onChange(updateGradeMap(value, row.id, 'letter', event.target.value))}
                  />
                  <span className="eoa-configuration-maps-to">maps to</span>
                  <TextField
                    className="eoa-configuration-narrow-input"
                    placeholder="Number grade"
                    inputMode="decimal"
                    value={row.number}
                    error={Boolean(numberHelperText)}
                    helperText={numberHelperText}
                    onChange={(event) => onChange(updateGradeMap(value, row.id, 'number', event.target.value))}
                  />
                  {value.gradeMaps.length > 1 && (
                    <IconButton
                      className="eoa-configuration-remove-button"
                      aria-label="Remove grade mapping"
                      onClick={() => onChange(removeGradeMap(value, row.id))}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </div>
              );
            })}
            <button type="button" className="eoa-configuration-add-link" onClick={() => onChange(addGradeMap(value))}>
              <img src={AddIcon} alt="" width={18} height={18} />
              Add grade
            </button>
          </div>
        </div>

        <div className="eoa-configuration-field">
          <span className="eoa-configuration-field-label">
            Do you ever include different credit systems on your transcripts (e.g. dual enrollment courses using college
            credit system instead of high school)?
          </span>
          <TextField
            fullWidth
            multiline
            minRows={8}
            placeholder={placeholder}
            value={value.multipleCreditSystems}
            onChange={(event) => onChange({ ...value, multipleCreditSystems: event.target.value })}
          />
        </div>
      </div>
    </Panel>
  );
};

export default GradingFields;
